<template>
  <div class="whatsapp-preview">
    <div class="whatsapp-header">
      <feather-icon
        icon="MessageCircleIcon"
        size="16"
        class="mr-1"
      />
      <span>Preview do WhatsApp</span>
    </div>

    <div
      v-if="!template"
      class="preview-empty"
    >
      <feather-icon
        icon="MessageSquareIcon"
        size="48"
        class="text-muted mb-2"
      />
      <p class="text-muted mb-0">
        Selecione um template para visualizar
      </p>
    </div>

    <div
      v-else
      class="preview-content"
    >
      <!-- Mensagem do WhatsApp -->
      <div class="whatsapp-message">
        <!-- Renderizar componentes do template -->
        <template v-if="template.components && template.components.length > 0">
          <div
            v-for="(component, index) in template.components"
            :key="index"
          >
            <!-- HEADER Component -->
            <div
              v-if="component.type === 'HEADER'"
              class="message-header"
            >
              <!-- Imagem -->
              <div
                v-if="component.format === 'IMAGE'"
                class="media-content"
              >
                <div
                  v-if="customImage"
                  class="image-preview-whatsapp"
                >
                  <img
                    :src="customImage"
                    alt="Preview da campanha"
                  >
                </div>
                <div
                  v-else
                  class="image-placeholder"
                >
                  <feather-icon
                    icon="ImageIcon"
                    size="32"
                    class="text-muted"
                  />
                  <small class="text-muted mt-1">Imagem</small>
                </div>
              </div>
              <!-- Vídeo -->
              <div
                v-else-if="component.format === 'VIDEO'"
                class="media-content"
              >
                <div
                  v-if="customImage"
                  class="image-preview-whatsapp"
                >
                  <img
                    :src="customImage"
                    alt="Preview do vídeo (thumbnail)"
                  >
                </div>
                <div
                  v-else
                  class="video-placeholder"
                >
                  <feather-icon
                    icon="VideoIcon"
                    size="32"
                    class="text-muted"
                  />
                  <small class="text-muted mt-1">Vídeo</small>
                </div>
              </div>
              <!-- Documento -->
              <div
                v-else-if="component.format === 'DOCUMENT'"
                class="media-content"
              >
                <div class="document-placeholder">
                  <feather-icon
                    icon="FileTextIcon"
                    size="32"
                    class="text-muted"
                  />
                  <small class="text-muted mt-1">Documento</small>
                </div>
              </div>
              <!-- Texto do header -->
              <div
                v-else-if="component.format === 'TEXT'"
                class="header-text"
              >
                <strong>{{ parseTemplateText(component.text) }}</strong>
              </div>
            </div>

            <!-- CAROUSEL Component -->
            <div
              v-else-if="component.type === 'CAROUSEL' && carouselCards.length"
              class="message-carousel"
            >
              <div class="carousel-cards-preview">
                <div
                  v-for="(card, cardIndex) in carouselCards"
                  :key="cardIndex"
                  class="carousel-card-preview-item"
                  @click="card.buttonUrlFull ? copyCardButtonLink(card.buttonUrlFull) : $emit('click-card', cardIndex)"
                >
                  <div
                    v-if="card.previewImage"
                    class="carousel-card-image"
                  >
                    <img
                      :src="card.previewImage"
                      :alt="`Card ${cardIndex + 1}`"
                    >
                  </div>
                  <div
                    v-else
                    class="carousel-card-placeholder"
                  >
                    <feather-icon
                      icon="ImageIcon"
                      size="24"
                      class="text-muted"
                    />
                    <small class="text-muted mt-1">Card {{ cardIndex + 1 }} — Clique para adicionar</small>
                  </div>
                  <div
                    v-if="card.buttonUrlFull"
                    class="carousel-card-button-url mt-1"
                  >
                    <feather-icon
                      icon="LinkIcon"
                      size="12"
                    />
                    <small class="text-truncate d-block">{{ card.buttonUrlFull }}</small>
                  </div>
                </div>
              </div>
            </div>

            <!-- BODY Component -->
            <div
              v-else-if="component.type === 'BODY'"
              class="message-body"
            >
              {{ parseTemplateText(component.text) }}
            </div>

            <!-- FOOTER Component -->
            <div
              v-else-if="component.type === 'FOOTER'"
              class="message-footer"
            >
              <small class="text-muted">{{ component.text }}</small>
            </div>

            <!-- BUTTONS Component -->
            <div
              v-else-if="component.type === 'BUTTONS'"
              class="message-buttons"
            >
              <div
                v-for="(button, btnIndex) in component.buttons"
                :key="btnIndex"
                class="whatsapp-button-container"
              >
                <div class="whatsapp-button">
                  <feather-icon
                    :icon="getButtonIcon(button.type)"
                    size="14"
                    class="mr-1"
                  />
                  {{ button.text }}
                </div>
                <!-- Mostrar URL abaixo do botão se for tipo URL -->
                <div
                  v-if="button.type === 'URL' && getButtonUrl(button)"
                  class="button-url"
                >
                  <feather-icon
                    icon="LinkIcon"
                    size="12"
                    class="mr-1"
                  />
                  <small>{{ getButtonUrl(button) }}</small>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- Fallback para estrutura antiga (sem components array) -->
        <template v-else>
          <!-- Header do template (se houver) -->
          <div
            v-if="template.header"
            class="message-header"
          >
            <!-- Imagem/Vídeo/Documento -->
            <div
              v-if="template.header.format === 'IMAGE'"
              class="media-content"
            >
              <div
                v-if="customImage"
                class="image-preview-whatsapp"
              >
                <img
                  :src="customImage"
                  alt="Preview da campanha"
                >
              </div>
              <div
                v-else
                class="image-placeholder"
              >
                <feather-icon
                  icon="ImageIcon"
                  size="32"
                  class="text-muted"
                />
                <small class="text-muted mt-1">Imagem</small>
              </div>
            </div>
            <div
              v-else-if="template.header.format === 'VIDEO'"
              class="media-content"
            >
              <div
                v-if="customImage"
                class="image-preview-whatsapp"
              >
                <img
                  :src="customImage"
                  alt="Preview do vídeo (thumbnail)"
                >
              </div>
              <div
                v-else
                class="video-placeholder"
              >
                <feather-icon
                  icon="VideoIcon"
                  size="32"
                  class="text-muted"
                />
                <small class="text-muted mt-1">Vídeo</small>
              </div>
            </div>
            <div
              v-else-if="template.header.format === 'DOCUMENT'"
              class="media-content"
            >
              <div class="document-placeholder">
                <feather-icon
                  icon="FileTextIcon"
                  size="32"
                  class="text-muted"
                />
                <small class="text-muted mt-1">Documento</small>
              </div>
            </div>
            <!-- Texto do header -->
            <div
              v-else-if="template.header.format === 'TEXT'"
              class="header-text"
            >
              <strong>{{ parseTemplateText(template.header.text) }}</strong>
            </div>
          </div>

          <!-- Body do template -->
          <div
            v-if="template.body"
            class="message-body"
          >
            {{ parseTemplateText(template.body.text) }}
          </div>

          <!-- Footer do template (se houver) -->
          <div
            v-if="template.footer"
            class="message-footer"
          >
            <small class="text-muted">{{ template.footer.text }}</small>
          </div>

          <!-- Botões (se houver) -->
          <div
            v-if="template.buttons && template.buttons.length > 0"
            class="message-buttons"
          >
            <div
              v-for="(button, index) in template.buttons"
              :key="index"
              class="whatsapp-button-container"
            >
              <div class="whatsapp-button">
                <feather-icon
                  :icon="getButtonIcon(button.type)"
                  size="14"
                  class="mr-1"
                />
                {{ button.text }}
              </div>
              <!-- Mostrar URL abaixo do botão se for tipo URL -->
              <div
                v-if="button.type === 'URL' && getButtonUrl(button)"
                class="button-url"
              >
                <feather-icon
                  icon="LinkIcon"
                  size="12"
                  class="mr-1"
                />
                <small>{{ getButtonUrl(button) }}</small>
              </div>
            </div>
          </div>
        </template>
      </div>

      <!-- Informações do template -->
      <div class="template-info mt-3">
        <div class="info-item">
          <small class="text-muted">Nome:</small>
          <span class="ml-1">{{ template.name }}</span>
        </div>
        <div
          v-if="template.language"
          class="info-item"
        >
          <small class="text-muted">Idioma:</small>
          <span class="ml-1">{{ template.language }}</span>
        </div>
        <div
          v-if="template.category"
          class="info-item"
        >
          <small class="text-muted">Categoria:</small>
          <b-badge
            :variant="getCategoryVariant(template.category)"
            class="ml-1"
          >
            {{ template.category }}
          </b-badge>
        </div>
        <div
          v-if="template.status"
          class="info-item"
        >
          <small class="text-muted">Status:</small>
          <b-badge
            :variant="getStatusVariant(template.status)"
            class="ml-1"
          >
            {{ template.status }}
          </b-badge>
        </div>
      </div>

      <!-- Variáveis detectadas -->
      <div
        v-if="variaveis.length > 0"
        class="template-variables mt-3"
      >
        <small class="text-muted d-block mb-2">
          <feather-icon
            icon="CodeIcon"
            size="14"
            class="mr-1"
          />
          Variáveis detectadas:
        </small>
        <div class="variables-list">
          <b-badge
            v-for="(variavel, index) in variaveis"
            :key="index"
            variant="light-primary"
            class="mr-1 mb-1"
          >
            {{ variavel }}
          </b-badge>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { BBadge } from 'bootstrap-vue'
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue'

export default {
  name: 'WhatsAppPreview',
  components: {
    BBadge,
    FeatherIcon,
  },
  props: {
    template: {
      type: Object,
      default: null,
    },
    customImage: {
      type: String,
      default: null,
    },
    carouselCards: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    variaveis() {
      if (!this.template) return []

      const vars = new Set()

      // Se o template tem array de components (estrutura nova do WhatsApp)
      if (this.template.components && this.template.components.length > 0) {
        this.template.components.forEach(component => {
          if (component.text) {
            this.extrairVariaveis(component.text, vars)
          }
          // Buscar variáveis nos botões também
          if (component.buttons) {
            component.buttons.forEach(button => {
              if (button.text) {
                this.extrairVariaveis(button.text, vars)
              }
            })
          }
        })
      } else {
        // Estrutura antiga (fallback)
        // Buscar variáveis no header
        if (this.template.header?.text) {
          this.extrairVariaveis(this.template.header.text, vars)
        }

        // Buscar variáveis no body
        if (this.template.body?.text) {
          this.extrairVariaveis(this.template.body.text, vars)
        }

        // Buscar variáveis nos botões
        if (this.template.buttons) {
          this.template.buttons.forEach(button => {
            if (button.text) {
              this.extrairVariaveis(button.text, vars)
            }
          })
        }
      }

      return Array.from(vars).sort()
    },
  },
  methods: {
    copyCardButtonLink(url) {
      if (!url) return
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(() => {
          this.$bvToast?.toast('Link copiado para a área de transferência.', {
            title: 'Copiado',
            variant: 'success',
            solid: true,
          })
        }).catch(() => {
          this.$bvToast?.toast('Não foi possível copiar o link.', {
            title: 'Erro',
            variant: 'warning',
            solid: true,
          })
        })
      } else {
        this.$bvToast?.toast('Não foi possível copiar o link.', {
          title: 'Erro',
          variant: 'warning',
          solid: true,
        })
      }
    },
    parseTemplateText(text) {
      if (!text) return ''

      // Substituir variáveis {{1}}, {{2}}, etc. por valores exemplo
      return text.replace(/\{\{(\d+)\}\}/g, (match, num) => {
        const exemplos = {
          1: 'João',
          2: 'R$ 99,90',
          3: 'Produto X',
          4: '10%',
        }
        return exemplos[num] || match
      })
    },

    parseButtonUrl(url) {
      if (!url) return ''

      // Substituir variáveis {{1}}, {{2}}, etc. por valores exemplo
      const urlComExemplos = url.replace(/\{\{(\d+)\}\}/g, (match, num) => {
        const exemplos = {
          1: 'produto-exemplo',
          2: 'categoria',
          3: 'item',
        }
        return exemplos[num] || match
      })

      // Limitar o tamanho da URL exibida
      if (urlComExemplos.length > 60) {
        return `${urlComExemplos.substring(0, 57)}...`
      }

      return urlComExemplos
    },

    getButtonUrl(button) {
      if (!button || button.type !== 'URL') return null

      // 🆕 Se o botão tem example, usar o primeiro exemplo (URL completa)
      if (button.example && button.example.length > 0) {
        const exampleUrl = button.example[0]

        // Limitar o tamanho da URL exibida
        if (exampleUrl.length > 60) {
          return `${exampleUrl.substring(0, 57)}...`
        }

        return exampleUrl
      }

      // Senão, usar a URL do botão e substituir variáveis
      return this.parseButtonUrl(button.url)
    },

    extrairVariaveis(text, vars) {
      const matches = text.matchAll(/\{\{(\d+)\}\}/g)
      for (const match of matches) {
        vars.add(`{{${match[1]}}}`)
      }
    },

    getButtonIcon(type) {
      const icons = {
        QUICK_REPLY: 'CornerUpLeftIcon',
        URL: 'ExternalLinkIcon',
        PHONE_NUMBER: 'PhoneIcon',
        COPY_CODE: 'CopyIcon',
      }
      return icons[type] || 'CircleIcon'
    },

    getCategoryVariant(category) {
      const variants = {
        MARKETING: 'success',
        UTILITY: 'info',
        AUTHENTICATION: 'warning',
      }
      return variants[category] || 'secondary'
    },

    getStatusVariant(status) {
      const variants = {
        APPROVED: 'success',
        PENDING: 'warning',
        REJECTED: 'danger',
      }
      return variants[status] || 'secondary'
    },
  },
}
</script>

<style scoped>
.whatsapp-preview {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  background: #f8f9fa;
}

.whatsapp-header {
  background: #25d366;
  color: white;
  padding: 0.75rem 1rem;
  font-weight: 600;
  display: flex;
  align-items: center;
}

.preview-empty {
  padding: 3rem 1rem;
  text-align: center;
}

.preview-content {
  padding: 1rem;
}

.whatsapp-message {
  background: white;
  border-radius: 8px;
  padding: 0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  width: 100%;
}

.message-header {
  border-bottom: 1px solid #f0f0f0;
}

.media-content {
  padding: 0;
  text-align: center;
  background: #f8f9fa;
  overflow: hidden;
}

.image-placeholder,
.video-placeholder,
.document-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background: white;
  border: 2px dashed #dee2e6;
  border-radius: 6px;
  margin: 1rem;
}

.image-preview-whatsapp {
  width: 100%;
  max-width: 100%;
  position: relative;
  background: #000;
}

.image-preview-whatsapp img {
  width: 100%;
  height: auto;
  max-height: 300px;
  object-fit: cover;
  display: block;
  /* Proporção do WhatsApp: largura máxima com altura ajustável */
  aspect-ratio: 16 / 9;
}

.header-text {
  padding: 1rem;
  font-size: 1.1rem;
  border-bottom: 1px solid #f0f0f0;
}

.message-body {
  padding: 1rem;
  white-space: pre-wrap;
  line-height: 1.5;
  color: #333;
}

.message-footer {
  padding: 0 1rem 1rem;
  font-size: 0.85rem;
}

.message-buttons {
  border-top: 1px solid #f0f0f0;
  padding: 0.5rem;
}

.whatsapp-button-container {
  margin-bottom: 0.5rem;
}

.whatsapp-button-container:last-child {
  margin-bottom: 0;
}

.whatsapp-button {
  padding: 0.75rem 1rem;
  text-align: center;
  color: #25d366;
  font-weight: 600;
  cursor: pointer;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
  border: 1px solid #25d366;
}

.whatsapp-button:hover {
  background: #e8f5e9;
}

.button-url {
  padding: 0.5rem 1rem;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6c757d;
  background: #f8f9fa;
  border-radius: 4px;
  margin-top: 0.25rem;
  word-break: break-all;
}

.button-url small {
  font-size: 0.75rem;
  color: #6c757d;
}

/* Carrossel no preview */
.message-carousel {
  padding: 0.75rem;
  border-bottom: 1px solid #f0f0f0;
}

.carousel-cards-preview {
  display: flex;
  gap: 0.5rem;
  overflow-x: auto;
  padding-bottom: 0.25rem;
}

.carousel-card-preview-item {
  flex: 0 0 120px;
  cursor: pointer;
  border-radius: 8px;
  overflow: hidden;
  border: 2px solid #e9ecef;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.carousel-card-preview-item:hover {
  border-color: #25d366;
  box-shadow: 0 2px 8px rgba(37, 211, 102, 0.2);
}

.carousel-card-image {
  position: relative;
  width: 100%;
  padding-top: 100%;
  background: #f8f9fa;
  overflow: hidden;
}

.carousel-card-image img {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.carousel-card-replace-hint {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  font-size: 0.65rem;
  padding: 0.25rem;
  text-align: center;
}

.carousel-card-placeholder {
  width: 100%;
  min-height: 120px;
  background: #f8f9fa;
  border: 2px dashed #dee2e6;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  text-align: center;
}

.carousel-card-button-url {
  padding: 0.35rem 0.5rem;
  background: #f8f9fa;
  border-radius: 4px;
  font-size: 0.7rem;
  color: #6e6b7b;
  word-break: break-all;
}

.template-info {
  background: white;
  padding: 1rem;
  border-radius: 6px;
  border: 1px solid #e0e0e0;
}

.info-item {
  padding: 0.25rem 0;
  display: flex;
  align-items: center;
}

.template-variables {
  background: white;
  padding: 1rem;
  border-radius: 6px;
  border: 1px solid #e0e0e0;
}

.variables-list {
  display: flex;
  flex-wrap: wrap;
}
</style>
